import { conditionIndicesViewOptions, WALKABLE_KEYS } from '../constants';

const isAssetMatchingSearch = (asset, normalizedSearch) => {
    if (!asset || !normalizedSearch) {
        return false;
    }

    const assetName = (asset?.assetName || asset?.name || '').toString().toLowerCase();
    const assetId = (asset?.assetId || asset?.id || '').toString().toLowerCase();
    const assetSerial = (asset?.assetSerialNumber || asset?.serialNumber || '').toString().toLowerCase();

    return assetName.includes(normalizedSearch)
        || assetId.includes(normalizedSearch)
        || assetSerial.includes(normalizedSearch);
};

const walkAssets = (node, collector) => {
    if (!node) {
        return;
    }

    if (Array.isArray(node)) {
        node.forEach((item) => {
            walkAssets(item, collector);
        });
        return;
    }

    if (typeof node !== 'object') {
        return;
    }

    if (node?.assetId || node?.id) {
        collector.push(node);
    }

    WALKABLE_KEYS.forEach((key) => {
        walkAssets(node[key], collector);
    });
};

// Finds the first asset in a hierarchy/list tree matching name, id, or serial number.
export const findFirstMatchingAssetInHierarchy = (data, searchTerm) => {
    const normalizedSearch = searchTerm?.trim()?.toLowerCase();
    if (!normalizedSearch) {
        return null;
    }

    const assets = [];
    walkAssets(data, assets);

    return assets.find((asset) => isAssetMatchingSearch(asset, normalizedSearch)) || null;
};

// Finds the asset in a hierarchy/list tree with an EXACT assetId/id match (e.g. for deep-linked URLs).
export const findAssetByIdInHierarchy = (data, assetId) => {
    const targetId = assetId?.toString()?.trim();
    if (!targetId) {
        return null;
    }

    const assets = [];
    walkAssets(data, assets);

    return assets.find((asset) => (asset?.assetId ?? asset?.id)?.toString() === targetId) || null;
};

const nodeId = {
    powertrain: (id) => `powertrain-${id}`,
    location: (id) => `location-${id}`,
    singleAssets: (id) => `singleAssets-${id}`,
    site: (id) => `site-${id}`,
    singleAssetsSite: (id) => `singleAssets-site-${id}`,
    unassignedPowertrainsSite: (id) => `unassignedPowertrains-site-${id}`,
    subOrg: (id) => `subOrg-${id}`,
};

const hasValidId = (id) => id !== null && id !== undefined;

// Ids (matching useNodeExpansion) of every node rendering an expand/collapse toggle.
export const collectExpandableNodeIds = (organization, conditionIndicesView) => {
    const ids = [];

    const walkPowertrain = (powertrain) => {
        const powertrainId = powertrain?.powertrainId;
        if ((powertrain?.assets?.length || 0) > 0 && hasValidId(powertrainId)) {
            ids.push(nodeId.powertrain(powertrainId));
        }
    };

    const walkLocation = (location) => {
        const locationId = location?.locationId;
        const hasChildren = location?.assets?.length || location?.powertrains?.length || location?.subLocations?.length;
        if (hasChildren && hasValidId(locationId)) {
            ids.push(nodeId.location(locationId));
        }
        if ((location?.assets?.length || 0) > 0 && hasValidId(locationId)) {
            ids.push(nodeId.singleAssets(locationId));
        }
        (location?.powertrains || []).forEach(walkPowertrain);
        (location?.subLocations || []).forEach(walkLocation);
    };

    const walkSite = (site) => {
        const siteId = site?.siteId;
        const hasChildren = site?.notConfiguredAssets?.length || site?.notConfiguredPowertrains?.length || site?.locations?.length;
        if (hasChildren && hasValidId(siteId)) {
            ids.push(nodeId.site(siteId));
        }
        if (conditionIndicesView === conditionIndicesViewOptions.HIERARCHY && (site?.notConfiguredAssets?.length || 0) > 0 && hasValidId(siteId)) {
            ids.push(nodeId.singleAssetsSite(siteId));
        }
        if ((site?.notConfiguredPowertrains?.length || 0) > 0 && hasValidId(siteId)) {
            ids.push(nodeId.unassignedPowertrainsSite(siteId));
            site.notConfiguredPowertrains.forEach(walkPowertrain);
        }
        (site?.locations || []).forEach(walkLocation);
    };

    const walkSubOrg = (subOrg) => {
        const organizationId = subOrg?.organizationId;
        if ((subOrg?.sites?.length || 0) > 0 && hasValidId(organizationId)) {
            ids.push(nodeId.subOrg(organizationId));
        }
        (subOrg?.sites || []).forEach(walkSite);
    };

    (organization?.subOrganizations || []).forEach(walkSubOrg);
    (organization?.sites || []).forEach(walkSite);

    return ids;
};

// Ids of all ancestor nodes that must be expanded to make the asset visible (LIST or HIERARCHY view).
export const findAncestorNodeIds = (organization, asset, conditionIndicesView) => {
    if (!organization || !asset) { return []; }
    const targetId = asset?.assetId?.toString();
    if (!targetId) { return []; }
    const isHierarchy = conditionIndicesView === conditionIndicesViewOptions.HIERARCHY;

    const hasAsset = (assets) => (assets || []).some(a => a.assetId?.toString() === targetId);

    const inPowertrains = (powertrains, ...prefix) => {
        for (const pt of powertrains || []) {
            if (hasAsset(pt?.assets)) {
                if (hasValidId(pt?.powertrainId)) {
                    return [...prefix, nodeId.powertrain(pt?.powertrainId)];
                }
                return [...prefix];
            }
        }
        return null;
    };

    const walkLocation = (location) => {
        const locationId = location?.locationId;
        const locId = hasValidId(locationId) ? nodeId.location(locationId) : null;
        if (hasAsset(location?.assets) && locId) { return [locId, nodeId.singleAssets(locationId)]; }
        const inPt = locId ? inPowertrains(location?.powertrains, locId) : inPowertrains(location?.powertrains);
        if (inPt) { return inPt; }
        for (const sub of location?.subLocations || []) {
            const found = walkLocation(sub);
            if (found) {
                if (locId) {
                    return [locId, ...found];
                }
                return found;
            }
        }
        return null;
    };

    const walkSite = (site) => {
        const rawSiteId = site?.siteId;
        const siteId = hasValidId(rawSiteId) ? nodeId.site(rawSiteId) : null;
        if (hasAsset(site?.notConfiguredAssets)) {
            if (!siteId) { return null; }
            return isHierarchy ? [siteId, nodeId.singleAssetsSite(rawSiteId)] : [siteId];
        }
        const inPt = siteId
            ? inPowertrains(site?.notConfiguredPowertrains, siteId, nodeId.unassignedPowertrainsSite(rawSiteId))
            : inPowertrains(site?.notConfiguredPowertrains);
        if (inPt) { return inPt; }
        for (const loc of site?.locations || []) {
            const found = walkLocation(loc);
            if (found) {
                if (siteId) {
                    return [siteId, ...found];
                }
                return found;
            }
        }
        return null;
    };

    for (const subOrg of organization?.subOrganizations || []) {
        for (const site of subOrg?.sites || []) {
            const found = walkSite(site);
            if (found) {
                if (hasValidId(subOrg?.organizationId)) {
                    return [nodeId.subOrg(subOrg?.organizationId), ...found];
                }
                return found;
            }
        }
    }
    for (const site of organization?.sites || []) {
        const found = walkSite(site);
        if (found) { return found; }
    }
    return [];
};

// Node id for an asset leaf row (used only by the selection-dot indicator).
const assetLeafNodeId = (assetId) => `asset-${assetId}`;

// Node ids (asset leaf + ancestors) to show the "has selected signal" indicator for each selected asset id.
export const collectSelectionNodeIds = (hierarchyData, selectedAssetIds, conditionIndicesView) => {
    const nodeIds = new Set();
    if (!hierarchyData || !selectedAssetIds?.length) { return nodeIds; }

    selectedAssetIds.forEach((assetId) => {
        if (assetId === null || assetId === undefined || assetId === '') { return; }
        const ancestorIds = findAncestorNodeIds(hierarchyData, { assetId }, conditionIndicesView);
        if (!ancestorIds?.length) { return; }
        ancestorIds.forEach((id) => nodeIds.add(id));
        nodeIds.add(assetLeafNodeId(assetId));
    });

    return nodeIds;
};
